import { forwardRef, InputHTMLAttributes, ReactNode, useEffect, useRef } from 'react'

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type'> {
    checked?: boolean
    onChange?: (checked: boolean) => void
    label?: ReactNode
    indeterminate?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(({ checked, onChange, label, indeterminate, disabled, className = '', ...props }, ref) => {
    const internalRef = useRef<HTMLInputElement>(null)
    const resolvedRef = (ref as React.RefObject<HTMLInputElement>) || internalRef

    useEffect(() => {
        if (resolvedRef && 'current' in resolvedRef && resolvedRef.current) {
            resolvedRef.current.indeterminate = !!indeterminate
        }
    }, [indeterminate, resolvedRef])

    return (
        <label className={`inline-flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'} ${className}`}>
            <input
                ref={resolvedRef}
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                disabled={disabled}
                className="h-4 w-4 shrink-0 cursor-pointer rounded border border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-2 focus:ring-blue-600/20 disabled:cursor-not-allowed"
                {...props}
            />
            {label && <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>}
        </label>
    )
})

Checkbox.displayName = 'Checkbox'
